/* eslint-disable max-lines */
import {
  type AiTakeAVideoConfig,
  AttendanceVerificationMethod,
} from "@clipboard-health/contract-backend-main";
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import { useSkippableWait } from "@src/appV2/redesign/utils/useSkippableWait";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  type TimekeepingData,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { SHIFT_DETAILS_TIMEKEEPING_FULL_PATH } from "../../path";
import {
  type ComplianceProofVideo,
  TimeclockVideoProofFlow,
} from "../TimeclockComplianceVideoProof";
import { VideoValidationFailedBottomSheet } from "../TimeclockComplianceVideoProof/dialogs/VideoValidationFailedBottomSheet";
import { useValidateVideo } from "../TimeclockComplianceVideoProof/hooks/useValidateVideo";
import { AcceptNonInstantPayContent } from "./AcceptNonInstantPayContent";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { getClockInSuccessDescription } from "./utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "./utils/getHasWorkerClockedInOnTime";
import { WorkplaceRulesReminderContent } from "./WorkplaceRulesReminderContent";

export interface ClockInWithVideoValidationBottomSheetProps {
  modalState: UseModalState;
  shift: Pick<WorkerShift, "_id" | "start">;
  attendanceVerificationMethod: AiTakeAVideoConfig;
  workerCredentials: {
    pin: string;
  };
  earliestGeofenceEntryTime?: Date;
  workerId: string;
  workplaceId: string;
  workerStage: WorkerStage;
  workplaceName: string;
  requiresBreakPolicyAcknowledgement: boolean;
  breakPolicyNoteId: string;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isSecondShiftInDouble: boolean;
  targetArrivalTime?: Date;
  shiftStartDate?: Date;
  timezone?: string;
  shiftBookedAt?: Date;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  isMissedPunchRequestEnabled: boolean;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
}

type TimeclockComplianceStep =
  | "video-recording"
  | "requesting-clock-in"
  | "clock-in-success"
  | "accept-non-instant-pay"
  | "requesting-non-instant-pay"
  | "workplace-rules"
  | "video-validation-failed";

export function ClockInWithVideoValidationBottomSheet(
  props: ClockInWithVideoValidationBottomSheetProps
) {
  const {
    modalState,
    workerId,
    workerStage,
    workplaceId,
    shift,
    attendanceVerificationMethod,
    workerCredentials,
    workplaceName,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    breakPolicyNoteId,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isSecondShiftInDouble,
    earliestGeofenceEntryTime,
    targetArrivalTime,
    shiftStartDate,
    timezone,
    shiftBookedAt,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
    isMissedPunchRequestEnabled,
    onTimekeepingActionCompleted,
  } = props;

  const shiftId = shift._id;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const [step, setStep] = useState<TimeclockComplianceStep>("video-recording");
  const complianceProofRef = useRef<ComplianceProofVideo>();

  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();
  const { mutateAsync: validateVideo } = useValidateVideo();

  const { wait, skip } = useSkippableWait();

  const shiftTimekeepingPath = generatePath(SHIFT_DETAILS_TIMEKEEPING_FULL_PATH, {
    shiftId,
  });

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const clockInTime = postClockInShift?.clockInOut?.start
    ? parseISO(postClockInShift.clockInOut.start)
    : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate: parseISO(shift.start),
    clockInTime,
  });

  const abortFlowAndClose = useCallback(() => {
    skip();
    modalState.closeModal();
    setStep("video-recording");
  }, [modalState, skip]);

  const completeSuccessFlow = useCallback(
    async (timekeepingData?: TimekeepingData) => {
      setStep("clock-in-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);

      if (timekeepingData) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: timekeepingData.clockInOut,
                },
              }
            : undefined
        );
        history.replace(shiftTimekeepingPath);
      }
    },
    [wait, queryClient, shiftId, history, shiftTimekeepingPath]
  );

  const onClockInSuccess = useCallback(
    async (timekeepingData: TimekeepingData) => {
      onTimekeepingActionCompleted();

      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else {
        await completeSuccessFlow(timekeepingData);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      onTimekeepingActionCompleted,
    ]
  );

  async function handleRecordClockIn(
    actionCheckType: ActionCheckType,
    videoProof: ComplianceProofVideo | undefined
  ) {
    setStep("requesting-clock-in");

    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_IN,
        shiftActionCheck: actionCheckType,
        verificationMethod: AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
        complianceProof: videoProof,
      });

      await onClockInSuccess(postMutationShift);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockInType: "timeclock-compliance-with-video-validation",
        error: errorMessage,
      });

      showErrorToast(errorMessage);
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.TIMECLOCK_VIDEO_PROOF_COMPLIANCE_FAILURE,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: "TIMECLOCK_VIDEO_PROOF_COMPLIANCE_FAILURE",
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  if (requiresBreakPolicyAcknowledgement) {
    return (
      <BreakConfirmationBottomSheet
        action={NoteAcknowledgementAction.CLOCK_IN}
        isLoading={false}
        modalState={modalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        breakPolicyNoteId={breakPolicyNoteId}
        onAcknowledge={async () => {
          setRequiresBreakPolicyAcknowledgement(false);
        }}
      />
    );
  }

  if (step === "video-recording" && modalState.modalIsOpen) {
    return (
      <TimeclockVideoProofFlow
        workplaceName={workplaceName}
        timeclockLocation={attendanceVerificationMethod.timeclockLocationString}
        timeclockPin={workerCredentials.pin}
        timeclockGifUrl={attendanceVerificationMethod.timeclockGifUrl}
        workplaceId={workplaceId}
        shiftId={shiftId}
        workerId={workerId}
        clockAction="clock-in"
        videoConfig={DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG}
        linearProgressDurationSeconds={20}
        isSkipTimeclockUsageEnabled={false}
        timeClockUsageWarning="Usage of the time clock is required to avoid payment delays for this shift."
        videoReviewNotice={
          <Text variant="body1">The video will be reviewed automatically by our system.</Text>
        }
        onSuccess={async (videoProof: ComplianceProofVideo) => {
          complianceProofRef.current = videoProof;

          try {
            const { isValid } = await validateVideo({
              shiftId,
              fileKey: videoProof.fileKey,
              bucketName: videoProof.bucketName,
            });

            if (isValid) {
              await handleRecordClockIn(ActionCheckType.LOCATION, videoProof);
            } else {
              setStep("video-validation-failed");
            }
          } catch (error: unknown) {
            const errorMessage =
              error instanceof Error ? error.message : "Failed to upload video, please try again.";

            showErrorToast(errorMessage);
            setStep("video-recording");
          }
        }}
        onCancel={() => {
          abortFlowAndClose();
        }}
      />
    );
  }

  return (
    <BottomSheet modalState={modalState} canDismiss={false}>
      {step === "requesting-clock-in" && (
        <BottomSheetLoadingContent
          title="Recording your clock in"
          description="This may take a moment"
        />
      )}

      {step === "clock-in-success" && (
        <TimekeepingActionSuccessContent
          title="Have a great shift!"
          description={getClockInSuccessDescription({
            hasWorkerClockedInOnTime,
            isSecondShiftInDouble,
            earliestGeofenceEntryTime,
            isTimesheetEditDisabled,
            shouldEnforceNfcTimesheetBounds,
            isArrivalTimeFeatureEnabled,
            targetArrivalTime,
            shiftStartDate,
            clockInTime,
            timezone,
            shiftBookedAt,
            policy: clipboardScoreProfile?.policy,
            isClipboardScoreFreezeEnabled,
            clipboardScoreFreezeReason,
            workerStage,
          })}
          onClose={() => {
            skip();
          }}
        />
      )}

      {step === "workplace-rules" && workplaceRules && (
        <WorkplaceRulesReminderContent
          workplaceRules={workplaceRules}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onContinue={async () => {
            await completeSuccessFlow(postClockInShift);
          }}
        />
      )}

      {step === "accept-non-instant-pay" && (
        <AcceptNonInstantPayContent
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onAccept={async () => {
            setStep("requesting-non-instant-pay");
            await convertToNonInstantPay();
          }}
          onCancel={() => {
            abortFlowAndClose();
          }}
        />
      )}

      {step === "requesting-non-instant-pay" && (
        <BottomSheetLoadingContent
          title="Converting to non-instant pay"
          description="This may take a moment"
        />
      )}

      {step === "video-validation-failed" && (
        <VideoValidationFailedBottomSheet
          modalState={modalState}
          onSeeInstructions={() => {
            setStep("video-recording");
          }}
          onCannotUseTimeclock={() => {
            setStep("accept-non-instant-pay");
          }}
        />
      )}
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
